/**
 * 客戶端訂閱配送員即時位置（AppSync graphql-ws 協定，不用 Amplify）。
 *
 * 協定參照 lpg-admin-web src/client/appsyncRealtimeClient.js，差別：
 * - 授權是 app-api 發的通行證（lpgtrk.…），不是 Cognito idToken。
 * - 一條連線只訂閱一位配送員。
 * - 自帶 keep-alive 看門狗：LINE webview 在行動網路下常出現沒有 close 事件的死連線。
 * - 查詢字串固定，必須與 lpg-iot-core-lambda trackingAuthorizer 的 ALLOWED_SUBSCRIPTION
 *   逐字相同（空白不計），改任何一個字都會被授權拒絕。
 */
export const CUSTOMER_LOCATION_SUBSCRIPTION =
  "subscription OnCustomerDriverLocation($courierId: ID!) { onDriverLocationUpdated(courierId: $courierId) { courierId lat lng bearing activity isMoving ts } }";

export interface RawDriverLocation {
  courierId: string;
  lat: number;
  lng: number;
  bearing?: number | null;
  activity?: string | null;
  isMoving?: boolean | null;
  ts: number;
}

type WebSocketCtor = { new (url: string, protocol: string): WebSocket; OPEN: number };

export interface LocationSubscriptionOptions {
  wssUrl: string;
  httpHost: string;
  token: string;
  courierId: string;
  onLocation: (loc: RawDriverLocation) => void;
  /** 握手完成、訂閱已送出 */
  onReady?: () => void;
  /**
   * 這筆訂閱被 AppSync 接受（收到本訂閱 id 的 start_ack）。
   * 訂閱被拒發生在 connection_ack 之後，所以「連線成功」要以這裡為準。
   */
  onSubscribed?: () => void;
  /** 通行證被拒（過期、被竄改）。呼叫端應換新通行證重連。 */
  onAuthError?: () => void;
  /** 非主動關閉的斷線。呼叫端決定要不要重連。 */
  onClose?: () => void;
  WebSocketImpl?: WebSocketCtor;
  makeId?: () => string;
  /** 握手完成前的看門狗逾時；ack 之後改用伺服器給的 connectionTimeoutMs。測試用。 */
  connectionTimeoutMs?: number;
}

/**
 * 多久沒收到任何訊息就判定連線已死。
 *
 * AppSync 會在 connection_ack 的 payload 給 connectionTimeoutMs（通常 5 分鐘），
 * 並在閒置時固定送 ka；收到 ack 後就改用它給的值。這個常數是握手完成前的預設值。
 */
const DEFAULT_CONNECTION_TIMEOUT_MS = 300_000;

const defaultId = (): string =>
  typeof crypto !== "undefined" && typeof crypto.randomUUID === "function"
    ? crypto.randomUUID()
    : // 舊版 LINE webview 沒有 randomUUID；訂閱 id 只需在這條連線內唯一
      `sub-${Date.now()}-${Math.random().toString(36).slice(2)}`;

export function openLocationSubscription(opts: LocationSubscriptionOptions): { close(): void } {
  const WS = opts.WebSocketImpl ?? (WebSocket as unknown as WebSocketCtor);
  const auth = { host: opts.httpHost, Authorization: opts.token };
  const url = `${opts.wssUrl}?header=${btoa(JSON.stringify(auth))}&payload=${btoa("{}")}`;
  const ws = new WS(url, "graphql-ws");
  const subId = (opts.makeId ?? defaultId)();
  let ready = false;
  let closedByUs = false;
  let timeoutMs = opts.connectionTimeoutMs ?? DEFAULT_CONNECTION_TIMEOUT_MS;
  let watchdog: ReturnType<typeof setTimeout> | null = null;

  const clearWatchdog = () => {
    if (watchdog) clearTimeout(watchdog);
    watchdog = null;
  };

  /**
   * 每收到一則訊息（含 ka）就重新上膛。
   *
   * 行動網路上常見的不是乾淨的斷線，而是「socket 還開著、對面已經沒聲音」——
   * 這時 onclose 不會觸發，重連整套都不會啟動，地圖只會靜靜地凍結在最後一個點，
   * 要等使用者切出去再切回來才復原。逾時就自己 close()，走既有的 onClose 重連路徑。
   */
  const armWatchdog = () => {
    clearWatchdog();
    watchdog = setTimeout(() => {
      watchdog = null;
      ws.close();
    }, timeoutMs);
  };

  ws.onopen = () => {
    // 握手是 connection_init → connection_ack；ack 之前不能送訂閱
    ws.send(JSON.stringify({ type: "connection_init" }));
    // 連上了但 ack 一直不來也算死連線，所以看門狗從這裡就開始
    armWatchdog();
  };

  ws.onmessage = (ev: MessageEvent) => {
    // 收到任何東西都代表連線還活著，先重新上膛再管內容是什麼
    armWatchdog();
    let msg: any;
    try {
      msg = JSON.parse(String(ev.data));
    } catch {
      return;
    }
    switch (msg?.type) {
      case "connection_ack": {
        ready = true;
        // 伺服器說多久沒訊息就該視為斷線，以它為準（沒給才留用預設值）
        const serverTimeout = msg.payload?.connectionTimeoutMs;
        if (typeof serverTimeout === "number" && serverTimeout > 0) {
          timeoutMs = serverTimeout;
          armWatchdog();
        }
        ws.send(
          JSON.stringify({
            id: subId,
            type: "start",
            payload: {
              data: JSON.stringify({
                query: CUSTOMER_LOCATION_SUBSCRIPTION,
                variables: { courierId: opts.courierId },
              }),
              extensions: { authorization: auth },
            },
          }),
        );
        opts.onReady?.();
        return;
      }
      case "data": {
        const loc = msg.payload?.data?.onDriverLocationUpdated;
        if (loc) opts.onLocation(loc as RawDriverLocation);
        return;
      }
      case "connection_error":
        opts.onAuthError?.();
        return;
      case "start_ack":
        if (msg.id === subId) opts.onSubscribed?.();
        return;
      case "error":
        // 訂閱被拒（例如通行證過期後重送 start）
        if (msg.id === subId) opts.onAuthError?.();
        return;
      default:
        // ka（keep-alive）、complete —— ka 的作用是上面那行 armWatchdog，這裡不必再處理
        return;
    }
  };

  ws.onerror = () => {};

  ws.onclose = () => {
    ready = false;
    clearWatchdog();
    if (!closedByUs) opts.onClose?.();
  };

  return {
    close() {
      closedByUs = true;
      clearWatchdog();
      if (ready && ws.readyState === WS.OPEN) {
        ws.send(JSON.stringify({ type: "stop", id: subId }));
      }
      ws.close();
    },
  };
}
